import { useMemo } from "react";
import { useWizard } from "../CalendarForm";
import { SectionHeader } from "../components/inputs";
import { 
  calculateAllParameters,
  generateCalendarLines,
  organizeLinesByMonth,
} from "../services/CalculationService";

// Icons
const SummaryIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// Time unit labels
const TIME_UNITS = {
  daily: "days",
  weekly: "weeks",
  monthly: "months",
};

// Rhythm labels
const RHYTHM_LABELS = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

/**
 * Parameter Row Component
 * Displays a single parameter with label and value
 */
const ParameterRow = ({ label, value, unit = "" }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-sm font-medium text-gray-900">
      {value}{unit && <span className="text-gray-500 ml-1">{unit}</span>}
    </span>
  </div>
);

/**
 * Parameter Group Component
 * Displays a group of parameters with a title
 */
const ParameterGroup = ({ title, children }) => (
  <div className="mb-4 last:mb-0">
    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{title}</h5>
    <div className="bg-white rounded-lg border border-gray-200 px-4">
      {children}
    </div>
  </div>
);

/**
 * Format number to fixed decimals
 */
const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return Number(value).toFixed(decimals);
};

/**
 * Step3Summary Component
 * 
 * Third step of the wizard showing summary of inputs and calculated values.
 */
export default function StepSummary() {
  const { formData } = useWizard();

  // Get the config based on goal type
  const config = formData.goal === "bulk" ? formData.bulkConfig : formData.cutConfig;
  const goalLabel = formData.goal === "bulk" ? "Bulk" : formData.goal === "cut" ? "Cut" : "Metabolic";
  const color = formData.goal === "bulk" ? "blue" : formData.goal === "cut" ? "rose" : "amber";

  // Calculate all parameters
  const calculatedParams = useMemo(() => {
    if (!config) return null;
    return calculateAllParameters(config);
  }, [config]);

  // Generate calendar lines
  const calendarLines = useMemo(() => {
    if (!config || !calculatedParams) return [];
    return generateCalendarLines(config, calculatedParams);
  }, [config, calculatedParams]);

  // Organize lines by month
  const calendarMonths = useMemo(() => {
    if (!calendarLines || calendarLines.length === 0) return [];
    return organizeLinesByMonth(calendarLines, config?.rhythm || "weekly");
  }, [calendarLines, config?.rhythm]);

  // Get time unit based on rhythm
  const timeUnit = config?.rhythm ? TIME_UNITS[config.rhythm] : "weeks";
  const rhythmLabel = config?.rhythm ? RHYTHM_LABELS[config.rhythm] : "Weekly";

  if (!config) {
    return (
      <div className="text-center py-12 text-gray-500">
        No configuration data available. Please complete the previous steps.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Summary"
        description={`Review your ${goalLabel} configuration`}
        icon={SummaryIcon}
        color={color}
      />

      {/* Input Parameters Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Input Parameters</h4>

        <ParameterGroup title="Body Measurements">
          <ParameterRow 
            label="Morning Weight" 
            value={config.morningWeight || "—"} 
            unit="kg" 
          />
          <ParameterRow 
            label="Night Weight" 
            value={config.nightWeight || "—"} 
            unit="kg" 
          />
          <ParameterRow 
            label="Current Fat %" 
            value={config.currentFatPercentage || "—"} 
            unit="%" 
          />
          <ParameterRow 
            label="Target Fat %" 
            value={config.targetFatPercentage || "—"} 
            unit="%" 
          />
          <ParameterRow 
            label="Lean Mass Gain Target" 
            value={config.leanMassGain || "—"} 
            unit="kg" 
          />
        </ParameterGroup>

        <ParameterGroup title="Timing">
          <ParameterRow 
            label="Rhythm" 
            value={rhythmLabel} 
          />
          <ParameterRow 
            label="Duration" 
            value={config.time || "—"} 
            unit={timeUnit} 
          />
        </ParameterGroup>

        <ParameterGroup title="Skin Folds">
          {config.skinFolds?.map((fold, index) => (
            fold.name && (
              <ParameterRow 
                key={index}
                label={fold.name} 
                value={`${fold.current || "—"} → ${fold.target || "—"}`} 
                unit="mm" 
              />
            )
          ))}
          {(!config.skinFolds || config.skinFolds.every(f => !f.name)) && (
            <ParameterRow label="No skin folds defined" value="—" />
          )}
        </ParameterGroup>

        <ParameterGroup title="Body Perimeters">
          {config.perimeters?.map((perimeter, index) => (
            perimeter.name && (
              <ParameterRow 
                key={index}
                label={perimeter.name} 
                value={`${perimeter.current || "—"} → ${perimeter.target || "—"}`} 
                unit="cm" 
              />
            )
          ))}
          {(!config.perimeters || config.perimeters.every(p => !p.name)) && (
            <ParameterRow label="No perimeters defined" value="—" />
          )}
        </ParameterGroup>
      </div>

      {/* Calculated Parameters Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Calculated Parameters</h4>

        {calculatedParams && (
          <>
            <ParameterGroup title="Weight Targets">
              <ParameterRow 
                label="Body Weight Target" 
                value={formatNumber(calculatedParams.bodyWeightTarget)} 
                unit="kg" 
              />
              <ParameterRow 
                label="Lean Mass Target" 
                value={formatNumber(calculatedParams.leanMassTarget)} 
                unit="kg" 
              />
              <ParameterRow 
                label="Lean Mass Ratio" 
                value={formatNumber(calculatedParams.leanMassRatio, 4)} 
                unit={`kg/${timeUnit.slice(0, -1)}`} 
              />
            </ParameterGroup>

            <ParameterGroup title="Body Composition - Current">
              <ParameterRow 
                label="Current Body Fat" 
                value={formatNumber(calculatedParams.currentBodyFat)} 
                unit="kg" 
              />
              <ParameterRow 
                label="Current Lean Mass" 
                value={formatNumber(calculatedParams.currentLeanMass)} 
                unit="kg" 
              />
            </ParameterGroup>

            <ParameterGroup title="Body Composition - Target">
              <ParameterRow 
                label="Body Fat End" 
                value={formatNumber(calculatedParams.bodyFatEnd)} 
                unit="kg" 
              />
              <ParameterRow 
                label="Fat Difference" 
                value={formatNumber(calculatedParams.fatDifference)} 
                unit="kg" 
              />
              <ParameterRow 
                label="Fat Percentage Difference" 
                value={formatNumber(calculatedParams.fatPercentageDifference)} 
                unit="%" 
              />
            </ParameterGroup>

            <ParameterGroup title="Ratios & Rhythms">
              <ParameterRow 
                label="Fat Difference Ratio" 
                value={formatNumber(calculatedParams.fatDifferenceRatio, 4)} 
                unit="%" 
              />
              <ParameterRow 
                label="Total Caloric Deficit" 
                value={formatNumber(calculatedParams.totalCaloricDeficit, 0)} 
                unit="kcal" 
              />
              <ParameterRow 
                label="Weight Difference Rhythm" 
                value={formatNumber(calculatedParams.weightDifferenceRhythm, 2)} 
                unit={`g/${timeUnit.slice(0, -1)}`} 
              />
            </ParameterGroup>

            <ParameterGroup title="Parameter Ratios">
              {config.skinFolds?.[0]?.name && (
                <ParameterRow 
                  label={`${config.skinFolds[0].name} Ratio`} 
                  value={formatNumber(calculatedParams.firstParamRatio, 4)} 
                  unit={`mm/${timeUnit.slice(0, -1)}`} 
                />
              )}
              {config.skinFolds?.[1]?.name && (
                <ParameterRow 
                  label={`${config.skinFolds[1].name} Ratio`} 
                  value={formatNumber(calculatedParams.secondParamRatio, 4)} 
                  unit={`mm/${timeUnit.slice(0, -1)}`} 
                />
              )}
              {config.perimeters?.[0]?.name && (
                <ParameterRow 
                  label={`${config.perimeters[0].name} Ratio`} 
                  value={formatNumber(calculatedParams.thirdParamRatio, 4)} 
                  unit={`cm/${timeUnit.slice(0, -1)}`} 
                />
              )}
              {config.perimeters?.[1]?.name && (
                <ParameterRow 
                  label={`${config.perimeters[1].name} Ratio`} 
                  value={formatNumber(calculatedParams.fourthParamRatio, 4)} 
                  unit={`cm/${timeUnit.slice(0, -1)}`} 
                />
              )}
              <ParameterRow 
                label="Skinfold/Perimeter Rhythm Coefficient" 
                value={calculatedParams.skinfoldPerimeterRhythmCoefficient} 
              />
            </ParameterGroup>
          </>
        )}
      </div>

      {/* Preview Section - Calendar */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Preview</h4>
        
        {calendarMonths.length > 0 ? (
          <div className="space-y-6">
            {calendarMonths.map((month, monthIndex) => (
              <div key={monthIndex} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Month Header */}
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-700">{month.name}</h5>
                </div>
                
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Period</th>
                        {config.skinFolds?.[0]?.name && (
                          <th className="px-3 py-2 text-right font-medium text-gray-500">
                            {config.skinFolds[0].name}
                            <span className="text-gray-400 ml-1">(mm)</span>
                          </th>
                        )}
                        {config.skinFolds?.[1]?.name && (
                          <th className="px-3 py-2 text-right font-medium text-gray-500">
                            {config.skinFolds[1].name}
                            <span className="text-gray-400 ml-1">(mm)</span>
                          </th>
                        )}
                        {config.perimeters?.[0]?.name && (
                          <th className="px-3 py-2 text-right font-medium text-gray-500">
                            {config.perimeters[0].name}
                            <span className="text-gray-400 ml-1">(cm)</span>
                          </th>
                        )}
                        {config.perimeters?.[1]?.name && (
                          <th className="px-3 py-2 text-right font-medium text-gray-500">
                            {config.perimeters[1].name}
                            <span className="text-gray-400 ml-1">(cm)</span>
                          </th>
                        )}
                        <th className="px-3 py-2 text-right font-medium text-gray-500">
                          Fat %
                          <span className="text-gray-400 ml-1">(kg)</span>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500">
                          AM Weight
                          <span className="text-gray-400 ml-1">(kg)</span>
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-500">
                          PM Weight
                          <span className="text-gray-400 ml-1">(kg)</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {month.lines.map((line, lineIndex) => (
                        <tr 
                          key={lineIndex} 
                          className={`border-b border-gray-100 last:border-0 ${
                            lineIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-600 font-medium">
                            {line.periodLabel}
                          </td>
                          {config.skinFolds?.[0]?.name && (
                            <td className="px-3 py-2 text-right text-gray-900 font-mono">
                              {formatNumber(line.firstParam, 1)}
                            </td>
                          )}
                          {config.skinFolds?.[1]?.name && (
                            <td className="px-3 py-2 text-right text-gray-900 font-mono">
                              {formatNumber(line.secondParam, 1)}
                            </td>
                          )}
                          {config.perimeters?.[0]?.name && (
                            <td className="px-3 py-2 text-right text-gray-900 font-mono">
                              {formatNumber(line.thirdParam, 1)}
                            </td>
                          )}
                          {config.perimeters?.[1]?.name && (
                            <td className="px-3 py-2 text-right text-gray-900 font-mono">
                              {formatNumber(line.fourthParam, 1)}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right text-gray-900 font-mono">
                            {formatNumber(line.fatPercentage, 1)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-mono">
                            {formatNumber(line.morningWeight, 1)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-mono">
                            {formatNumber(line.nightWeight, 1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm">No calendar data to preview.</p>
            <p className="text-xs text-gray-400 mt-1">Please complete the configuration in the previous step.</p>
          </div>
        )}
      </div>
    </div>
  );
}